import { type AlertColor } from "@mui/lab";

import { ALL_CATEGORIES } from "./constants";
import {
  type Breakdown,
  type Category,
  DisplayStatus,
  type Insight,
  InsightPriority,
  InsightsSortBy,
  type Risks,
  type RisksBreakdownValue,
  RiskTypeKey,
  type SavingsPeriod,
  type StatusDetails,
} from "./types";

export const getInsightId = (insight: Insight): string => `${insight.providerId}#${insight.key}`;

export const totalFromBreakdown = (isOptimized: boolean, breakdown: Breakdown): number | null => {
  let savings = 0;

  if (!isOptimized && breakdown) {
    for (const savingsData of breakdown.data) {
      savings += savingsData.value;
    }
  }

  return savings;
};

export const totalCustomProblems = (insight: Insight): number | null => {
  if (!insight.results?.customProblems?.breakdown) {
    return null;
  }

  return totalFromBreakdown(insight.results.customProblems.isOptimized, insight.results.customProblems.breakdown);
};

export const totalRisks = (insight: Insight, riskType: RiskTypeKey): number | null => {
  const risks = insight.results?.[riskType];
  if (!risks?.breakdown) {
    return null;
  }

  if (risks.isOptimized) {
    return 0;
  }

  let total = 0;
  risks.breakdown.data.forEach((e) => {
    const value = e.value.critical + e.value.high + e.value.medium + e.value.low;
    total += value;
  });
  return total;
};

const calculateBreakdown = (
  breakdownData:
    | {
        value: RisksBreakdownValue;
      }[]
    | undefined
): RisksBreakdownValue | null => {
  if (!breakdownData) {
    return null;
  }

  const breakdown: RisksBreakdownValue = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
  };

  breakdownData.forEach((el) => {
    breakdown.critical += el.value.critical;
    breakdown.high += el.value.high;
    breakdown.medium += el.value.medium;
    breakdown.low += el.value.low;
  });

  return breakdown;
};

export const totalRisksBreakdown = (insight: Insight | null, riskType: RiskTypeKey): RisksBreakdownValue | null => {
  if (!insight) {
    return null;
  }

  return calculateBreakdown(insight.results?.[riskType]?.breakdown?.data);
};

export const totalPotentialDailySavings = (insight: Insight): number | null => {
  if (!insight.results?.potentialDailySavings?.breakdown) {
    return null;
  }

  return totalFromBreakdown(
    insight.results?.potentialDailySavings?.isOptimized,
    insight.results?.potentialDailySavings?.breakdown
  );
};

const comparePriority = (a: Insight, b: Insight): number => {
  const priorityOrder = [InsightPriority.High, InsightPriority.Medium, InsightPriority.Low];

  const priorityA = a.priority ? priorityOrder.indexOf(a.priority) : -1;
  const priorityB = b.priority ? priorityOrder.indexOf(b.priority) : -1;
  if (priorityA !== priorityB) {
    if (priorityA === -1) return 1;
    if (priorityB === -1) return -1;
    return priorityA - priorityB;
  }
  return 0;
};

const compareStatus = (a: Insight, b: Insight, activeStatusesOnly = true): number => {
  const allStatusOrder = [
    DisplayStatus.ACTIONABLE,
    DisplayStatus.ACKNOWLEDGED,
    DisplayStatus.IN_PROGRESS,
    DisplayStatus.PERMISSIONS_NEEDED,
    DisplayStatus.OPTIMIZED,
    DisplayStatus.DISMISSED,
  ];

  const activeStatusOrder = [DisplayStatus.ACTIONABLE, DisplayStatus.ACKNOWLEDGED, DisplayStatus.IN_PROGRESS];

  const statusOrder = activeStatusesOnly ? activeStatusOrder : allStatusOrder;

  const statusA = a.displayStatus ? statusOrder.indexOf(a.displayStatus) : -1;
  const statusB = b.displayStatus ? statusOrder.indexOf(b.displayStatus) : -1;
  if (statusA !== statusB) {
    if (statusA === -1) return 1;
    if (statusB === -1) return -1;
    return statusA - statusB;
  }
  return 0;
};

const compareEasyWin = (a: Insight, b: Insight): number => {
  const easyWinA = !!a.easyWinDescription;
  const easyWinB = !!b.easyWinDescription;
  if (easyWinA !== easyWinB) {
    return easyWinA ? -1 : 1;
  }
  return 0;
};

const sortDefault = (insights: Insight[]): Insight[] =>
  [...insights].sort((a, b) => {
    const statusResult = compareStatus(a, b, false);
    if (statusResult !== 0) return statusResult;

    const priorityResult = comparePriority(a, b);
    if (priorityResult !== 0) return priorityResult;

    const easyWinResult = compareEasyWin(a, b);
    if (easyWinResult !== 0) return easyWinResult;

    return 0;
  });

const sortByPriority = (insights: Insight[]): Insight[] =>
  [...insights].sort((a, b) => {
    // Sort insights with existing priority first
    const hasPriorityA = !!a.priority;
    const hasPriorityB = !!b.priority;
    if (hasPriorityA !== hasPriorityB) {
      return hasPriorityA ? -1 : 1;
    }
    // Sort by status
    const statusResult = compareStatus(a, b);
    if (statusResult !== 0) return statusResult;

    // Sort by priority
    return comparePriority(a, b);
  });

const sortByPotentialDailySavings = (insights: Insight[]): Insight[] =>
  [...insights].sort((a, b) => {
    const savingsA = totalPotentialDailySavings(a) ?? 0;
    const savingsB = totalPotentialDailySavings(b) ?? 0;

    // Sort insights with existing savings first
    const hasSavingsA = savingsA > 0;
    const hasSavingsB = savingsB > 0;
    if (hasSavingsA !== hasSavingsB) {
      return hasSavingsA ? -1 : 1;
    }
    // Sort by status
    const statusResult = compareStatus(a, b);
    if (statusResult !== 0) return statusResult;

    // Sort by savings
    return savingsB - savingsA;
  });

const getAllRisks = (insight: Insight): number => {
  const riskTypes = Object.values(RiskTypeKey) as RiskTypeKey[];

  return riskTypes.reduce((sum, riskType) => sum + (totalRisks(insight, riskType) || 0), 0);
};

const sortByRisks = (insights: Insight[]) => {
  const getRisks = (insight: Insight) => getAllRisks(insight);

  return [...insights].sort((a, b) => {
    const risksA = getRisks(a);
    const risksB = getRisks(b);

    // Sort insights with existing risks first
    const hasRisksA = risksA > 0;
    const hasRisksB = risksB > 0;
    if (hasRisksA !== hasRisksB) {
      return hasRisksA ? -1 : 1;
    }
    // Sort by status
    const statusResult = compareStatus(a, b);
    if (statusResult !== 0) return statusResult;

    // Sort by risks
    return risksB - risksA;
  });
};

export const sortInsights = (insights: Insight[], sortBy: InsightsSortBy | ""): Insight[] => {
  const sortedInsights = sortDefault(insights);
  switch (sortBy) {
    case InsightsSortBy.PRIORITY:
      return sortByPriority(sortedInsights);
    case InsightsSortBy.SAVINGS:
      return sortByPotentialDailySavings(sortedInsights);
    case InsightsSortBy.RISKS:
      return sortByRisks(sortedInsights);
  }
  return sortedInsights;
};

export const calculateTotalSavingsForTimePeriod = (insights: Insight[] | undefined, period: SavingsPeriod): number => {
  const multiplier = period === "daily" ? 1 : period === "monthly" ? 30 : 365;
  return (
    insights?.reduce((total, insight) => {
      const dailySavings =
        insight.results?.potentialDailySavings?.breakdown?.data.reduce((sum, dataItem) => sum + dataItem.value, 0) || 0;
      return total + dailySavings * multiplier;
    }, 0) ?? 0
  );
};

export const getAlert = (
  insight: Insight,
  statusDetails: StatusDetails | null = null
): { severity: AlertColor; label: string } | null => {
  // alerts for custom insights
  if (insight.customInsightAttributes && insight.displayStatus === DisplayStatus.DISMISSED) {
    return {
      severity: "info",
      label: statusDetails
        ? `This insight was dismissed by ${statusDetails.userDisplayName} on ${statusDetails.date}.`
        : "This insight was dismissed.",
    };
  }

  if (insight.customInsightAttributes && insight.displayStatus === "optimized") {
    return {
      severity: "success",
      label: statusDetails
        ? `This insight was marked as optimized by ${statusDetails.userDisplayName} on ${statusDetails.date}.`
        : "This insight was marked as optimized.",
    };
  }

  // alerts for automated insights
  if (!insight.customInsightAttributes && insight.displayStatus === DisplayStatus.DISMISSED) {
    return {
      severity: "info",
      label: "This insight was automatically dismissed by DoiT Cloud Intelligence™.",
    };
  }

  if (!insight.customInsightAttributes && insight.displayStatus === "optimized") {
    return {
      severity: "success",
      label: "This insight is currently optimized and no further action is required.",
    };
  }

  return null;
};

const riskSeverityMapping = {
  "aws-trusted-advisor": {
    critical: "Critical",
    high: "High (red)",
    medium: "Medium (yellow)",
    low: "Low",
  },
  gcp: {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  },
  custom: {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  },
  default: {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  },
};

export const convertInsightWithRisks = (
  baseRisks: Risks | null,
  mapping: { [key: string]: string }
): { breakdown: Breakdown } | null => {
  if (!baseRisks?.breakdown) {
    return null;
  }

  const data = baseRisks.breakdown.data.map((data) => {
    const severity1orMore = Object.entries(data.value).filter(([_, value]) => value >= 1);
    return severity1orMore.map(([key, value]) => ({
      baseValue: 0,
      dimensionValues: [...data.dimensionValues, mapping[key]],
      value,
    }));
  });

  return {
    ...baseRisks,
    breakdown: {
      dimensions: [...(baseRisks.breakdown?.dimensions || []), "Severity"],
      data: data.flat(),
    },
  };
};

export const convertToDisplayRiskBreakdown = (providerId: string, result: Risks | null): Breakdown | null => {
  if (result === null) {
    return null;
  }

  const mapping =
    (riskSeverityMapping[providerId] as {
      [key: string]: string;
    }) ?? riskSeverityMapping.default;

  const converted = convertInsightWithRisks(result, mapping);

  return converted ? converted.breakdown : null;
};

export const normalizeInsightCategories = (results: Insight[] | undefined): Insight[] =>
  results?.map((datum) => {
    const categories = datum.categories;
    const validCategories = categories?.filter((category): category is Category =>
      ALL_CATEGORIES.some((ac) => ac.toLowerCase() === category.toLowerCase())
    );

    return {
      ...datum,
      categories: validCategories,
    };
  }) || [];

export const calculateSavings = (savings: number) => savings * 365.25;

type CalculatePermissionMetricsRes = {
  permissionsCoverage: number;
  grantedPermissionsInsights: number;
};

export const calculatePermissionsMetrics = (
  totalInsightsNum: number,
  permissionsNeededInsightsNum: number
): CalculatePermissionMetricsRes => {
  if (totalInsightsNum === 0) {
    return {
      permissionsCoverage: 0,
      grantedPermissionsInsights: 0,
    };
  }
  const grantedPermissionsInsights = totalInsightsNum - permissionsNeededInsightsNum;
  const permissionsCoverage = Math.round((grantedPermissionsInsights / totalInsightsNum) * 100);

  return {
    permissionsCoverage,
    grantedPermissionsInsights,
  };
};
